import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const WISFeatureModulesSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [activeModule, setActiveModule] = useState(0);

  const modules = [
    {
      id: 0,
      name: {
        en: "Inventory Management",
        hi: "इन्वेंटरी प्रबंधन"
      },
      description: {
        en: "Real-time tracking of aluminum profiles, billets, and finished products",
        hi: "एल्यूमीनियम प्रोफाइल, बिलेट्स और तैयार उत्पादों की रियल-टाइम ट्रैकिंग"
      },
      features: [
        {
          en: "Barcode/QR code scanning",
          hi: "बारकोड/QR कोड स्कैनिंग"
        },
        {
          en: "Batch tracking and traceability",
          hi: "बैच ट्रैकिंग और ट्रेसेबिलिटी"
        },
        {
          en: "Multi-location inventory",
          hi: "मल्टी-लोकेशन इन्वेंटरी"
        },
        {
          en: "Automated stock alerts",
          hi: "स्वचालित स्टॉक अलर्ट"
        }
      ],
      roi: {
        en: "25% reduction in inventory costs",
        hi: "इन्वेंटरी लागत में 25% कमी"
      },
      icon: "Package"
    },
    {
      id: 1,
      name: {
        en: "Material Tracking",
        hi: "मैटेरियल ट्रैकिंग"
      },
      description: {
        en: "End-to-end visibility of material movement from receipt to dispatch",
        hi: "प्राप्ति से डिस्पैच तक मैटेरियल मूवमेंट की पूर्ण दृश्यता"
      },
      features: [
        {
          en: "Real-time location tracking",
          hi: "रियल-टाइम स्थान ट्रैकिंग"
        },
        {
          en: "Movement history logs",
          hi: "मूवमेंट हिस्ट्री लॉग्स"
        },
        {
          en: "Temperature monitoring",
          hi: "तापमान निगरानी"
        },
        {
          en: "Quality check integration",
          hi: "गुणवत्ता जांच एकीकरण"
        }
      ],
      roi: {
        en: "40% faster material location",
        hi: "40% तेज़ मैटेरियल स्थान"
      },
      icon: "Activity"
    },
    {
      id: 2,
      name: {
        en: "Warehouse Layout Optimization",
        hi: "वेयरहाउस लेआउट अनुकूलन"
      },
      description: {
        en: "AI-powered warehouse design for maximum efficiency and space utilization",
        hi: "अधिकतम दक्षता और स्थान उपयोग के लिए AI-संचालित वेयरहाउस डिज़ाइन"
      },
      features: [
        {
          en: "3D warehouse visualization",
          hi: "3D वेयरहाउस विज़ुअलाइज़ेशन"
        },
        {
          en: "Optimal bin placement",
          hi: "अनुकूलित बिन प्लेसमेंट"
        },
        {
          en: "Traffic flow analysis",
          hi: "ट्रैफिक फ्लो विश्लेषण"
        },
        {
          en: "Space utilization reports",
          hi: "स्थान उपयोग रिपोर्ट"
        }
      ],
      roi: {
        en: "30% better space utilization",
        hi: "30% बेहतर स्थान उपयोग"
      },
      icon: "Grid"
    },
    {
      id: 3,
      name: {
        en: "Automated Reordering",
        hi: "स्वचालित पुनर्व्यवस्था"
      },
      description: {
        en: "Smart reorder points based on demand patterns and lead times",
        hi: "मांग पैटर्न और लीड टाइम के आधार पर स्मार्ट रीऑर्डर पॉइंट्स"
      },
      features: [
        {
          en: "AI demand forecasting",
          hi: "AI मांग पूर्वानुमान"
        },
        {
          en: "Supplier integration",
          hi: "आपूर्तिकर्ता एकीकरण"
        },
        {
          en: "Automated PO generation",
          hi: "स्वचालित PO जेनरेशन"
        },
        {
          en: "Lead time optimization",
          hi: "लीड टाइम अनुकूलन"
        }
      ],
      roi: {
        en: "20% reduction in stockouts",
        hi: "स्टॉकआउट में 20% कमी"
      },
      icon: "RefreshCw"
    }
  ];

  const t = (text) => text[currentLanguage] || text.en;

  return (
    <section id="wis-features" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {currentLanguage === 'en' ?'Core WIS Modules' :'मुख्य WIS मॉड्यूल'
            }
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {currentLanguage === 'en' ?'Explore our comprehensive warehouse management modules designed specifically for aluminum manufacturers' :'एल्यूमीनियम निर्माताओं के लिए विशेष रूप से डिज़ाइन किए गए हमारे व्यापक वेयरहाउस प्रबंधन मॉड्यूल का अन्वेषण करें'
            }
          </p>
        </div>

        {/* Tabbed Interface */}
        <div className="bg-surface-50 rounded-2xl p-8">
          {/* Module Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 bg-white rounded-xl p-2">
            {modules.map((module, index) => (
              <button
                key={module.id}
                onClick={() => setActiveModule(index)}
                className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-all duration-300 ${
                  activeModule === index
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                    : 'text-text-secondary hover:bg-surface-100'
                }`}
              >
                <Icon name={module.icon} size={20} />
                <span className="font-medium">{t(module.name)}</span>
              </button>
            ))}
          </div>

          {/* Active Module Content */}
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Module Details */}
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-primary mb-4">
                  {t(modules[activeModule].name)}
                </h3>
                <p className="text-text-secondary leading-relaxed">
                  {t(modules[activeModule].description)}
                </p>
              </div>

              {/* Features List */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-primary">
                  {currentLanguage === 'en' ? 'Key Features' : 'मुख्य विशेषताएं'}
                </h4>
                <ul className="space-y-3">
                  {modules[activeModule].features.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-success-100 rounded-full flex items-center justify-center">
                        <Icon name="Check" size={14} color="var(--color-success)" />
                      </div>
                      <span className="text-text-secondary">{t(feature)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* ROI Badge */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <Icon name="TrendingUp" size={20} color="var(--color-success)" />
                  <span className="font-semibold text-success">
                    {currentLanguage === 'en' ? 'ROI Impact:' : 'ROI प्रभाव:'}
                  </span>
                </div>
                <p className="text-lg font-bold text-success mt-2">
                  {t(modules[activeModule].roi)}
                </p>
              </div>
            </div>

            {/* Animated Workflow Demonstration */}
            <div className="bg-white rounded-xl shadow-lg p-6 border border-border">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full mb-4">
                  <Icon name={modules[activeModule].icon} size={32} />
                </div>
                <h4 className="text-lg font-semibold text-primary">
                  {currentLanguage === 'en' ? 'Workflow Demo' : 'वर्कफ़्लो डेमो'}
                </h4>
              </div>

              {/* Simulated Workflow Steps */}
              <div className="space-y-4">
                {modules[activeModule].features.slice(0, 3).map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 bg-surface-50 rounded-lg">
                    <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-text-secondary">{t(feature)}</p>
                    </div>
                    <div className="w-6 h-6 bg-success-100 rounded-full flex items-center justify-center">
                      <Icon name="Check" size={12} color="var(--color-success)" />
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <div className="text-center mt-6">
                <button className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl">
                  {currentLanguage === 'en' ? 'Try This Module' : 'इस मॉड्यूल को आज़माएं'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WISFeatureModulesSection;